import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import type { userState } from "../../interface/MainInterface";
import { HttpMonitoringApi } from "../../interface/monitoring-api";
import {
  buildDashboardArticleLookupParams,
  buildMonitoringDetailUrl,
  buildPressReleaseDetailUrl,
  normalizeDashboardSetting,
  pressReleaseItemId,
} from "./monitoringDashboardUtils";

type Tone = "red" | "amber" | "green" | "blue" | "teal" | "violet" | "gray";
type MonitoringType = "KEYWORD" | "PRESS_RELEASE";
type DashboardColor = "none" | "red" | "green" | "blue" | "yellow";
type ArticleRangeDays = 1 | 2 | 3 | 4 | 5 | 6 | 7;

interface ArticleDisplayFilter {
  negativeOnly?: boolean;
  revisionOnly?: boolean;
}

type ArticleDisplayFilterMode = "ALL" | "NEGATIVE" | "REVISION" | "NEGATIVE_OR_REVISION";

interface StatusTag {
  label: string;
  tone: Tone;
}

interface LatestArticle {
  id: string;
  title: string;
  publisher: string;
  reporter: string;
  collectedAt: string;
  searchTerm: string;
  summary: string;
  url?: string;
  tags: StatusTag[];
  sentiment?: string;
  revisionDetected?: boolean;
  reviewTags?: string[];
}

interface MonitoringCardData {
  id: string;
  title: string;
  category: string;
  type: MonitoringType;
  interval: string;
  status: "ACTIVE" | "PAUSED";
  isRunning: boolean;
  needsReviewCount: number;
  latestCount: number;
  sentimentEnabled?: boolean;
  revisionTrackingEnabled?: boolean;
  displayFilter?: ArticleDisplayFilter;
  tone: Tone;
  articles: LatestArticle[];
  articleError?: string | null;
}

interface PressReleaseSummary {
  key: string;
  company: string;
  title: string;
  count: number;
}

interface PressReleaseDashboardSummary {
  releasedPressCount: number;
  relatedArticleCount: number;
  negativeRelatedArticleCount: number;
  revisionDetectedArticleCount: number;
  pressReleaseSummaries: PressReleaseSummary[];
}

interface DashboardSummaryTotals {
  needsReviewCount: number;
  negativeArticleCount: number;
  latestArticleCount: number;
  revisionDetectedCount: number;
  notificationSentCount: number;
}

interface DashboardPartialError {
  key: string;
  message: string;
}

interface DashboardSettingDraft {
  visibleItemIds: string[];
  itemOrder: string[];
  cardColors: Record<string, DashboardColor>;
  articleLimits: Record<string, number>;
  articleDisplayFilters: Record<string, ArticleDisplayFilter>;
}

interface ApiResult {
  response: any | null;
  error: unknown | null;
}

interface MonitoringDashboardCustomizedPageProps {
  userState?: userState;
}

interface SortableDragHandleProps {
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
  setActivatorNodeRef: (element: HTMLElement | null) => void;
}

const monitoringApi = new HttpMonitoringApi();

const monitoringCards: MonitoringCardData[] = [];

const defaultItemOrder: string[] = [...monitoringCards.map((item) => item.id)];
const defaultCardColors: Record<string, DashboardColor> = {};
const publicAssetBaseUrl = process.env.PUBLIC_URL || "";
const monitoringListIconBaseUrl = `${publicAssetBaseUrl}/assets/monitoring-list`;
const monitoringDashboardIconBaseUrl = `${publicAssetBaseUrl}/assets/monitoring-dashboard`;
const keywordConfigIconBaseUrl = `${publicAssetBaseUrl}/assets/keyword-config`;
const commonImageBaseUrl = `${publicAssetBaseUrl}/images`;
const dashboardIcons = {
  refresh: `${commonImageBaseUrl}/icon-refresh.png`,
  settings: `${monitoringDashboardIconBaseUrl}/icon-settings.png`,
  addMonitoring: `${monitoringListIconBaseUrl}/icon-add.png`,
  latestArticle: `${monitoringDashboardIconBaseUrl}/icon-latest-article.png`,
  negativeArticle: `${keywordConfigIconBaseUrl}/negative-keyword.png`,
  revisionTracking: `${monitoringDashboardIconBaseUrl}/icon-revision-tracking.png`,
  notification: `${monitoringDashboardIconBaseUrl}/icon-notification.png`,
  detailMore: `${monitoringDashboardIconBaseUrl}/icon-detail-more.png`,
  settingsSearch: `${monitoringListIconBaseUrl}/icon-search.png`,
  settingsInfo: `${monitoringDashboardIconBaseUrl}/icon-settings-info.png`,
  settingsDropdown: `${monitoringListIconBaseUrl}/icon-select-arrow.png`,
};
const defaultArticleLimits: Record<string, number> = Object.fromEntries(
  monitoringCards.map((item) => [item.id, 3])
);
const defaultArticleDisplayFilters: Record<string, ArticleDisplayFilter> = {};

const emptySummaryTotals: DashboardSummaryTotals = {
  needsReviewCount: 0,
  negativeArticleCount: 0,
  latestArticleCount: 0,
  revisionDetectedCount: 0,
  notificationSentCount: 0,
};

const emptyPressReleaseSummary: PressReleaseDashboardSummary = {
  releasedPressCount: 0,
  relatedArticleCount: 0,
  negativeRelatedArticleCount: 0,
  revisionDetectedArticleCount: 0,
  pressReleaseSummaries: [],
};

const defaultArticleRangeDays: ArticleRangeDays = 1;

const articleRangeDayOptions: { value: ArticleRangeDays; label: string }[] = [
  { value: 1, label: "오늘" },
  { value: 2, label: "최근 2일" },
  { value: 3, label: "최근 3일" },
  { value: 4, label: "최근 4일" },
  { value: 5, label: "최근 5일" },
  { value: 6, label: "최근 6일" },
  { value: 7, label: "최근 7일" },
];

const normalizeArticleRangeDays = (value: unknown): ArticleRangeDays => {
  const numericValue = Number(value);
  return Number.isInteger(numericValue) && numericValue >= 1 && numericValue <= 7
    ? numericValue as ArticleRangeDays
    : defaultArticleRangeDays;
};

const formatArticleRangeLabel = (value: ArticleRangeDays) =>
  value === 1 ? "오늘" : `최근 ${value}일`;

const supportsRevisionTracking = (monitoringType: MonitoringType) =>
  monitoringType === "KEYWORD";

const getTodayKst = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

const toApiResult = async (request: Promise<any>): Promise<ApiResult> => {
  try {
    return { response: await request, error: null };
  } catch (error) {
    return { response: null, error };
  }
};

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

const getResponseErrorMessage = (response: any, fallback: string) =>
  response?.response?.error_msg || response?.response?.message || response?.message || fallback;

const isApiSuccess = (response: any) => String(response?.code) === "200";

const getApiFailureMessage = (result: ApiResult, fallback: string) => {
  if (result.error) return getErrorMessage(result.error, fallback);
  if (!isApiSuccess(result.response)) return getResponseErrorMessage(result.response, fallback);
  return null;
};

const cloneArticleDisplayFilters = (filters: Record<string, ArticleDisplayFilter>) =>
  Object.fromEntries(
    Object.entries(filters).map(([id, filter]) => [id, { ...filter }])
  );

const buildDashboardSettingDraft = ({
  visibleItemIds,
  itemOrder,
  cardColors,
  articleLimits,
  articleDisplayFilters,
}: DashboardSettingDraft): DashboardSettingDraft => ({
  visibleItemIds: [...visibleItemIds],
  itemOrder: [...itemOrder],
  cardColors: { ...cardColors },
  articleLimits: { ...articleLimits },
  articleDisplayFilters: cloneArticleDisplayFilters(articleDisplayFilters),
});

const sanitizeArticleDisplayFilter = (nextFilter: ArticleDisplayFilter) => {
  const sanitized: ArticleDisplayFilter = {};
  if (nextFilter.negativeOnly) sanitized.negativeOnly = true;
  if (nextFilter.revisionOnly) sanitized.revisionOnly = true;
  return sanitized;
};

const formatTime = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(11, 16) || value;
  return new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

const mapPressReleaseSummary = (item: any = {}, index = 0): PressReleaseSummary => ({
  key: item.pressReleaseId || item.key || `${index}`,
  company: item.company || "회사 미확인",
  title: item.title || "제목 없는 보도자료",
  count: Number(item.relatedArticleCount ?? item.count ?? 0),
});

const mapPressReleaseDashboardSummary = (item: any = {}): PressReleaseDashboardSummary => ({
  releasedPressCount: Number(item.releasedPressCount ?? 0),
  relatedArticleCount: Number(item.relatedArticleCount ?? 0),
  negativeRelatedArticleCount: Number(item.negativeRelatedArticleCount ?? 0),
  revisionDetectedArticleCount: Number(item.revisionDetectedArticleCount ?? 0),
  pressReleaseSummaries: Array.isArray(item.pressReleaseSummaries)
    ? item.pressReleaseSummaries.map(mapPressReleaseSummary)
    : [],
});

const getReviewTags = (item: any): StatusTag[] => {
  const tags = Array.isArray(item.reviewTags) ? item.reviewTags : [];
  const mapped = tags.map((tag: string) => {
    if (tag === "NEGATIVE") return { label: "부정", tone: "red" as Tone };
    if (tag === "REVISION_DETECTED") return { label: "수정", tone: "violet" as Tone };
    if (tag === "NOTIFICATION_SENT") return { label: "알림", tone: "green" as Tone };
    if (tag === "PRESS_RELEASE_RELATED") return { label: "보도자료", tone: "teal" as Tone };
    return { label: tag, tone: "gray" as Tone };
  });
  if (!mapped.length && item.sentiment === "NEGATIVE") mapped.push({ label: "부정", tone: "red" });
  if (!mapped.length) mapped.push({ label: "키워드", tone: "blue" });
  return mapped;
};

const mapArticleCard = (item: any): LatestArticle => ({
  id: item.articleId || item.id || item.sk,
  title: item.title || "제목 없는 기사",
  publisher: item.publisher || "매체 미확인",
  reporter: item.reporter || "기자 미확인",
  collectedAt: formatTime(item.detectedAt || item.collectedAt || item.publishedAt),
  searchTerm: (Array.isArray(item.matchedSearchTerms) && item.matchedSearchTerms[0]) || "검색어",
  summary: item.bodyPreview || item.description || "",
  url: item.url,
  tags: getReviewTags(item),
  sentiment: item.sentiment,
  revisionDetected: item.revisionDetected === true,
  reviewTags: Array.isArray(item.reviewTags) ? item.reviewTags : [],
});

const mapMonitoringCard = (monitoring: any, summary: any, articles: LatestArticle[], displayFilter: ArticleDisplayFilter): MonitoringCardData => ({
  id: monitoring.monitoringId || monitoring.id,
  title: monitoring.title || monitoring.name || "이름 없는 모니터링",
  category: monitoring.monitoringType === "PRESS_RELEASE" ? "보도자료 기반" : "키워드 기반",
  type: monitoring.monitoringType === "PRESS_RELEASE" ? "PRESS_RELEASE" : "KEYWORD",
  interval: `${Number(monitoring.runIntervalMinutes ?? monitoring.intervalMinutes ?? 15)}분 주기`,
  status: monitoring.status === "PAUSED" ? "PAUSED" : "ACTIVE",
  isRunning: summary?.isRunning === true,
  needsReviewCount: Number(summary?.needsReviewCount ?? 0),
  latestCount: articles.length,
  sentimentEnabled: monitoring.sentimentEnabled === true,
  revisionTrackingEnabled: supportsRevisionTracking(monitoring.monitoringType === "PRESS_RELEASE" ? "PRESS_RELEASE" : "KEYWORD"),
  displayFilter,
  tone: Number(summary?.needsReviewCount ?? 0) > 0 ? "red" : "blue",
  articles,
});

const MonitoringDashboardCustomizedPage = (props: MonitoringDashboardCustomizedPageProps) => {
  const navigate = useNavigate();
  const userId = props.userState?.id ?? "";
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [partialErrors, setPartialErrors] = useState<DashboardPartialError[]>([]);
  const [dashboardDate, setDashboardDate] = useState(getTodayKst);
  const [articleRangeDays, setArticleRangeDays] = useState<ArticleRangeDays>(defaultArticleRangeDays);
  const [dashboardCards, setDashboardCards] = useState<MonitoringCardData[]>([]);
  const [settingCards, setSettingCards] = useState<MonitoringCardData[]>([]);
  const [summaryTotals, setSummaryTotals] = useState<DashboardSummaryTotals>(emptySummaryTotals);
  const [pressReleaseSummary, setPressReleaseSummary] = useState<PressReleaseDashboardSummary>(emptyPressReleaseSummary);
  const [pressReleaseSummaryError, setPressReleaseSummaryError] = useState<string | null>(null);
  const [pressReleaseMonitoringId, setPressReleaseMonitoringId] = useState<string | null>(null);
  const [visibleItemIds, setVisibleItemIds] = useState<string[]>([]);
  const [itemOrder, setItemOrder] = useState(defaultItemOrder);
  const [cardColors, setCardColors] = useState<Record<string, DashboardColor>>(defaultCardColors);
  const [articleLimits, setArticleLimits] = useState<Record<string, number>>(defaultArticleLimits);
  const [articleDisplayFilters, setArticleDisplayFilters] = useState<Record<string, ArticleDisplayFilter>>(defaultArticleDisplayFilters);
  const [settingsDraft, setSettingsDraft] = useState<DashboardSettingDraft | null>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const hasPressReleaseMonitoring = Boolean(pressReleaseMonitoringId);
  const dashboardDragSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadDashboard = useCallback(async (isMounted: () => boolean = () => true) => {
    if (!userId) {
      setDashboardError("사용자 정보를 확인하지 못했습니다. 다시 로그인한 뒤 시도해주세요.");
      setLoading(false);
      return;
    }
    const requestDate = getTodayKst();
    setDashboardDate(requestDate);
    setLoading(true);
    setDashboardError(null);
    setPartialErrors([]);
    setPressReleaseSummaryError(null);
    try {
      const articleLookupParams = buildDashboardArticleLookupParams(articleRangeDays);
      const [dashboardResult, pressReleaseResult] = await Promise.all([
        toApiResult(
          monitoringApi.post({
            command: "get_monitoring_dashboard",
            userId,
            date: requestDate,
            ...articleLookupParams,
            reviewLimit: 20,
            includeArticlePreviews: true,
          })
        ),
        toApiResult(
          monitoringApi.post({
            command: "get_press_release_dashboard_summary",
            mode: "ACTIVE",
          })
        ),
      ]);
      if (!isMounted()) return;
      const dashboardFailureMessage = getApiFailureMessage(dashboardResult, "모니터링 대시보드를 불러오지 못했습니다.");
      if (dashboardFailureMessage) {
        setDashboardError(dashboardFailureMessage);
        return;
      }

      const response = dashboardResult.response;
      const data = response.response ?? {};
      const monitorings = Array.isArray(data.monitorings) ? data.monitorings : [];
      const summaries = Array.isArray(data.cardSummaries) ? data.cardSummaries : [];
      const hasArticlePreviews = data.articlePreviews && typeof data.articlePreviews === "object";
      const shouldUseDashboardArticlePreviews = hasArticlePreviews;
      const articlePreviews = hasArticlePreviews ? data.articlePreviews : {};
      const setting = data.dashboardSetting ?? {};
      const nextPartialErrors: DashboardPartialError[] = [];
      const pressReleaseFailureMessage = getApiFailureMessage(pressReleaseResult, "보도자료 요약을 불러오지 못했습니다.");
      const availableMonitorings = Array.isArray(data.availableMonitorings) ? data.availableMonitorings : null;
      let listMonitoringsFailureMessage: string | null = null;
      let allMonitorings: any[] = availableMonitorings ?? [];
      if (!availableMonitorings) {
        const listMonitoringsResult = await toApiResult(
          monitoringApi.post({
            command: "list_monitorings",
          })
        );
        if (!isMounted()) return;
        listMonitoringsFailureMessage = getApiFailureMessage(listMonitoringsResult, "모니터링 목록을 불러오지 못했습니다.");
        allMonitorings = !listMonitoringsFailureMessage
          ? ((listMonitoringsResult.response?.response?.monitorings ?? []) as any[])
          : [];
      }
      if (pressReleaseFailureMessage) {
        nextPartialErrors.push({
          key: "press-release-summary",
          message: `보도자료 요약 조회 실패: ${pressReleaseFailureMessage}`,
        });
      }
      if (listMonitoringsFailureMessage) {
        nextPartialErrors.push({
          key: "monitoring-list",
          message: `모니터링 목록 조회 실패: ${listMonitoringsFailureMessage}`,
        });
      }
      const nextArticleLimits = { ...defaultArticleLimits, ...(setting.articleLimits ?? {}) };
      const nextDisplayFilters = { ...defaultArticleDisplayFilters, ...(setting.articleDisplayFilters ?? {}) };
      const summaryByMonitoringId = new Map(summaries.map((item: any) => [item.monitoringId, item]));
      const pressReleaseMonitoring = allMonitorings.find((item) => item.monitoringType === "PRESS_RELEASE")
        ?? monitorings.find((item: any) => item.monitoringType === "PRESS_RELEASE");
      const resolvedPressReleaseMonitoringId = pressReleaseMonitoring
        ? (pressReleaseMonitoring.monitoringId || pressReleaseMonitoring.id || null)
        : (data.pressReleaseMonitoringId || null);

      const fallbackArticleResponses = shouldUseDashboardArticlePreviews
        ? []
        : await Promise.all(
            monitorings.map((monitoring: any) => {
              const monitoringId = monitoring.monitoringId || monitoring.id;
              const displayFilter = nextDisplayFilters[monitoringId] ?? {};
              return toApiResult(
                monitoringApi.post({
                  command: "list_monitoring_articles",
                  monitoringId,
                  date: requestDate,
                  limit: Number(nextArticleLimits[monitoringId] ?? 3),
                  negativeOnly: displayFilter.negativeOnly === true,
                  revisionOnly: displayFilter.revisionOnly === true,
                  ...articleLookupParams,
                })
              );
            })
          );
      if (!isMounted()) return;

      const articleFailureTitles: string[] = [];
      const nextCards = monitorings.map((monitoring: any, index: number) => {
        const monitoringId = monitoring.monitoringId || monitoring.id;
        const preview = articlePreviews[monitoringId] ?? {};
        const fallbackArticleResult = fallbackArticleResponses[index];
        const articleError = shouldUseDashboardArticlePreviews
          ? (preview.error ? String(preview.error) : null)
          : getApiFailureMessage(fallbackArticleResult, "최신 기사 목록을 불러오지 못했습니다.");
        const articles = !articleError
          ? ((shouldUseDashboardArticlePreviews
              ? (Array.isArray(preview.articles) ? preview.articles : [])
              : (fallbackArticleResult?.response?.response?.articles ?? [])) as any[]).map(mapArticleCard)
          : [];
        const card = mapMonitoringCard(monitoring, summaryByMonitoringId.get(monitoringId), articles, nextDisplayFilters[monitoringId] ?? {});
        card.latestCount = Number(
          shouldUseDashboardArticlePreviews
            ? (preview.totalCount ?? articles.length)
            : (fallbackArticleResult?.response?.response?.totalCount ?? articles.length)
        );
        card.articleError = articleError ? `기사 조회 실패: ${articleError}` : null;
        if (articleError) articleFailureTitles.push(card.title);
        return card;
      });
      const nextCardById = new Map(nextCards.map((card: MonitoringCardData) => [card.id, card]));
      const settingMonitorings = allMonitorings.filter((item) => item.monitoringType === "KEYWORD");
      const nextSettingCards = (settingMonitorings.length > 0 ? settingMonitorings : monitorings).map((monitoring: any) => {
        const monitoringId = monitoring.monitoringId || monitoring.id;
        return nextCardById.get(monitoringId)
          ?? mapMonitoringCard(
            monitoring,
            summaryByMonitoringId.get(monitoringId),
            [],
            nextDisplayFilters[monitoringId] ?? {}
          );
      });
      if (articleFailureTitles.length > 0) {
        nextPartialErrors.push({
          key: "monitoring-articles",
          message: `카드별 기사 조회 실패: ${articleFailureTitles.join(", ")}`,
        });
      }

      const normalizedSetting = normalizeDashboardSetting({
        setting,
        monitoringIds: nextSettingCards.map((item: MonitoringCardData) => item.id),
        pressReleaseMonitoringId: resolvedPressReleaseMonitoringId,
      });

      setDashboardCards(nextCards);
      setSettingCards(nextSettingCards);
      setSummaryTotals({
        ...emptySummaryTotals,
        ...(data.summaryTotals ?? {}),
      });
      setPressReleaseSummary(
        !pressReleaseFailureMessage
          ? mapPressReleaseDashboardSummary(pressReleaseResult.response?.response?.summary)
          : emptyPressReleaseSummary
      );
      setPressReleaseSummaryError(pressReleaseFailureMessage);
      setPressReleaseMonitoringId(resolvedPressReleaseMonitoringId);
      setArticleLimits({ ...defaultArticleLimits, ...normalizedSetting.articleLimits });
      setArticleDisplayFilters({ ...defaultArticleDisplayFilters, ...normalizedSetting.articleDisplayFilters });
      setVisibleItemIds(normalizedSetting.visibleItemIds);
      setItemOrder(normalizedSetting.itemOrder);
      setCardColors({ ...defaultCardColors, ...normalizedSetting.cardColors });
      setPartialErrors(nextPartialErrors);
    } catch (error) {
      if (isMounted()) setDashboardError(error instanceof Error ? error.message : "모니터링 대시보드를 불러오지 못했습니다.");
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [articleRangeDays, userId]);

  useEffect(() => {
    let mounted = true;
    loadDashboard(() => mounted);
    return () => {
      mounted = false;
    };
  }, [loadDashboard]);

  const totalNegativeArticles = summaryTotals.negativeArticleCount;
  const totalLatestArticles = summaryTotals.latestArticleCount;
  const articleRangeLabel = formatArticleRangeLabel(articleRangeDays);

  const visibleOrderedItems = [
    ...itemOrder.filter((id) => visibleItemIds.includes(id)),
    ...visibleItemIds.filter((id) => !itemOrder.includes(id)),
  ];
  const dashboardCardIds = new Set(dashboardCards.map((item) => item.id));
  const renderableVisibleOrderedItems = visibleOrderedItems.filter(
    (id) => dashboardCardIds.has(id) || (id === pressReleaseItemId && hasPressReleaseMonitoring)
  );
  const pressReleaseDisplayFilter = articleDisplayFilters[pressReleaseItemId] ?? {};
  const pressReleaseDetailUrl = buildPressReleaseDetailUrl(pressReleaseMonitoringId, pressReleaseDisplayFilter, articleRangeDays);

  const currentSettingDraft = settingsDraft ?? buildDashboardSettingDraft({
    visibleItemIds,
    itemOrder,
    cardColors,
    articleLimits,
    articleDisplayFilters,
  });

  const openSettingsDialog = () => {
    setSettingsError(null);
    setSettingsDraft(buildDashboardSettingDraft({
      visibleItemIds,
      itemOrder,
      cardColors,
      articleLimits,
      articleDisplayFilters,
    }));
    setSettingsOpen(true);
  };

  const closeSettingsDialog = () => {
    if (savingSettings) return;
    setSettingsError(null);
    setSettingsOpen(false);
    setSettingsDraft(null);
  };

  const applyDashboardSettingDraft = (draft: DashboardSettingDraft) => {
    setVisibleItemIds([...draft.visibleItemIds]);
    setItemOrder([...draft.itemOrder]);
    setCardColors({ ...defaultCardColors, ...draft.cardColors });
    setArticleLimits({ ...defaultArticleLimits, ...draft.articleLimits });
    setArticleDisplayFilters({ ...defaultArticleDisplayFilters, ...cloneArticleDisplayFilters(draft.articleDisplayFilters) });
  };

  const requestSaveDashboardSetting = async (draft: DashboardSettingDraft, fallbackMessage: string) => {
    const response = await monitoringApi.post({
      command: "save_dashboard_setting",
      userId,
      visibleItemIds: draft.visibleItemIds,
      itemOrder: draft.itemOrder,
      cardColors: draft.cardColors,
      articleLimits: draft.articleLimits,
      articleDisplayFilters: draft.articleDisplayFilters,
    });
    if (!isApiSuccess(response)) {
      throw new Error(response?.response?.error_msg ?? fallbackMessage);
    }
  };

  const saveDashboardSetting = async (draft: DashboardSettingDraft) => {
    if (!userId) {
      const message = "사용자 정보를 확인하지 못해 표시 설정을 저장할 수 없습니다.";
      setDashboardError(message);
      setSettingsError(message);
      return;
    }
    setSavingSettings(true);
    setDashboardError(null);
    setSettingsError(null);
    try {
      await requestSaveDashboardSetting(draft, "표시 설정을 저장하지 못했습니다.");
      applyDashboardSettingDraft(draft);
      setSettingsOpen(false);
      setSettingsDraft(null);
      setSettingsError(null);
      await loadDashboard();
    } catch (error) {
      const message = error instanceof Error ? error.message : "표시 설정을 저장하지 못했습니다.";
      setDashboardError(message);
      setSettingsError(message);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDashboardDragEnd = async (event: DragEndEvent) => {
    const activeId = String(event.active.id);
    const overId = event.over ? String(event.over.id) : null;
    setDraggingId(null);
    if (!overId || activeId === overId) return;
    if (!userId) {
      setDashboardError("사용자 정보를 확인하지 못해 카드 순서를 저장할 수 없습니다.");
      return;
    }

    const previousItemOrder = [...itemOrder];
    const visibleOrder = [
      ...itemOrder.filter((id) => visibleItemIds.includes(id)),
      ...visibleItemIds.filter((id) => !itemOrder.includes(id)),
    ];
    const fromIndex = visibleOrder.indexOf(activeId);
    const toIndex = visibleOrder.indexOf(overId);
    if (fromIndex < 0 || toIndex < 0) return;

    const nextVisibleOrder = arrayMove(visibleOrder, fromIndex, toIndex);
    const hiddenOrder = itemOrder.filter((id) => !nextVisibleOrder.includes(id));
    const nextItemOrder = [...nextVisibleOrder, ...hiddenOrder];
    setItemOrder(nextItemOrder);
    setDashboardError(null);

    try {
      await requestSaveDashboardSetting(
        {
          visibleItemIds,
          itemOrder: nextItemOrder,
          cardColors,
          articleLimits,
          articleDisplayFilters,
        },
        "카드 순서를 저장하지 못했습니다."
      );
    } catch (error) {
      setItemOrder(previousItemOrder);
      setDashboardError(error instanceof Error ? error.message : "카드 순서를 저장하지 못했습니다.");
    }
  };

  return (
    <Box sx={pageSx}>
      <Box sx={pageContentSx}>
        <Stack spacing={0}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} sx={dashboardHeaderSx}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={titleRowSx}>
                <Typography sx={pageTitleSx}>모니터링 대시보드</Typography>
                <Box component="span" sx={dashboardDateBadgeSx}>기준일 {dashboardDate}</Box>
              </Stack>
              <Typography sx={{ ...pageSubtitleSx, color: dashboardError ? "#b91c1c" : "#2E3341" }}>
                {loading
                  ? "모니터링 대시보드를 불러오는 중입니다."
                  : dashboardError ?? "선택한 모니터링의 최신 기사만 카드별로 확인합니다. 보도자료 모니터링은 집계 기준으로 관리합니다."}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={headerActionsSx}>
              <Button startIcon={<DashboardIcon src={dashboardIcons.refresh} sx={headerActionIconSx("refresh")} />} variant="outlined" sx={{ ...headerOutlineButtonSx, ...headerRefreshButtonSx }} onClick={() => loadDashboard()} disabled={loading}>
                <Box component="span" sx={headerButtonTextSx}>새로고침</Box>
              </Button>
              <Button startIcon={<DashboardIcon src={dashboardIcons.settings} sx={headerActionIconSx("settings")} />} variant="outlined" sx={{ ...headerOutlineButtonSx, ...headerSettingsButtonSx }} onClick={openSettingsDialog}>
                <Box component="span" sx={headerButtonTextSx}>표시 설정</Box>
              </Button>
              <Button startIcon={<DashboardIcon src={dashboardIcons.addMonitoring} sx={headerActionIconSx("add")} />} variant="contained" sx={headerPrimaryButtonSx} onClick={() => navigate("/monitoringCreate")}>
                <Box component="span" sx={headerButtonTextSx}>새 모니터링</Box>
              </Button>
            </Stack>
          </Stack>

          <Box sx={rangePanelSx}>
            <Typography component="span" sx={rangePanelLabelSx}>조회 기준</Typography>
            <RadioGroup
              aria-label="조회 기준"
              row
              value={String(articleRangeDays)}
              onChange={(_, value) => setArticleRangeDays(normalizeArticleRangeDays(value))}
              sx={rangeRadioGroupSx}
            >
              {articleRangeDayOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={String(option.value)}
                  control={
                    <Radio
                      size="small"
                      icon={<Box sx={rangeRadioIconSx} />}
                      checkedIcon={<Box sx={rangeRadioCheckedIconSx} />}
                      sx={rangeRadioSx}
                    />
                  }
                  label={option.label}
                  sx={rangeRadioOptionSx}
                />
              ))}
            </RadioGroup>
          </Box>

          {partialErrors.length > 0 && (
            <Stack spacing={0.75} sx={partialErrorsStackSx}>
              {partialErrors.map((error) => (
                <Alert key={error.key} severity="warning" sx={partialErrorAlertSx}>
                  {error.message}
                </Alert>
              ))}
            </Stack>
          )}

          {loading && dashboardCards.length > 0 && (
            <Box sx={refreshingNoticeSx}>
              <LinearProgress sx={refreshingProgressSx} />
              <Typography sx={refreshingTextSx}>최신 대시보드 데이터를 반영 중입니다.</Typography>
            </Box>
          )}

          <Box sx={summaryGridSx}>
            <MetricCard icon={<DashboardIcon src={dashboardIcons.latestArticle} sx={metricIconImageSx("blue")} />} label="최신 기사" value={`${totalLatestArticles}건`} caption="표시 모니터링 고유 기사" tone="blue" />
            <MetricCard icon={<DashboardIcon src={dashboardIcons.negativeArticle} sx={metricIconImageSx("red")} />} label="부정 기사" value={`${totalNegativeArticles}건`} caption="선택 모니터링" tone="red" />
            <MetricCard icon={<DashboardIcon src={dashboardIcons.revisionTracking} sx={metricIconImageSx("violet")} />} label="수정 추적" value={`${summaryTotals.revisionDetectedCount}건`} caption="전후 비교" tone="violet" />
            <MetricCard icon={<DashboardIcon src={dashboardIcons.notification} sx={metricIconImageSx("amber")} />} label="알림" value={`${summaryTotals.notificationSentCount}건`} caption="최근 발송" tone="amber" />
          </Box>

          <DndContext
            sensors={dashboardDragSensors}
            collisionDetection={closestCenter}
            onDragStart={(event) => setDraggingId(String(event.active.id))}
            onDragCancel={() => setDraggingId(null)}
            onDragEnd={handleDashboardDragEnd}
          >
            <SortableContext items={renderableVisibleOrderedItems} strategy={rectSortingStrategy}>
              <Box sx={dashboardGridSx}>
                {renderableVisibleOrderedItems.map((itemId) => {
                  const monitoring = dashboardCards.find((item) => item.id === itemId);

                  return (
                    <SortableDashboardItem key={itemId} id={itemId} active={draggingId === itemId}>
                      {(dragHandleProps) => (
                        <>
                          {monitoring ? (
                            <MonitoringCard
                              monitoring={monitoring}
                              color={cardColors[monitoring.id] ?? "blue"}
                              latestArticleLimit={articleLimits[monitoring.id] ?? 3}
                              articleRangeLabel={articleRangeLabel}
                              dragHandleProps={dragHandleProps}
                              onDetail={() => navigate(buildMonitoringDetailUrl({
                                monitoringId: monitoring.id,
                                tab: "articles",
                                negativeOnly: monitoring.displayFilter?.negativeOnly === true,
                                revisionOnly: monitoring.displayFilter?.revisionOnly === true,
                                articleRangeDays,
                              }))}
                            />
                          ) : itemId === pressReleaseItemId && hasPressReleaseMonitoring ? (
                            <PressReleaseAggregateCard
                              color={cardColors[pressReleaseItemId] ?? "none"}
                              summary={pressReleaseSummary}
                              errorMessage={pressReleaseSummaryError}
                              dragHandleProps={dragHandleProps}
                              onDetail={() => navigate(pressReleaseDetailUrl)}
                            />
                          ) : null}
                        </>
                      )}
                    </SortableDashboardItem>
                  );
                })}
                {renderableVisibleOrderedItems.length === 0 && (
                  <Paper variant="outlined" sx={emptySx}>
                    <Typography sx={{ color: "#2E3341", fontSize: 14, fontWeight: 600, lineHeight: "20px" }}>
                      {loading ? "모니터링 대시보드를 불러오는 중입니다." : "표시할 모니터링이 없습니다."}
                    </Typography>
                    <Typography sx={{ mt: 0.5, color: "#6F7279", fontSize: 14, fontWeight: 500, lineHeight: "20px" }}>
                      {loading ? "잠시만 기다려주세요." : "표시 설정에서 하나 이상의 모니터링을 선택해주세요."}
                    </Typography>
                  </Paper>
                )}
              </Box>
            </SortableContext>
          </DndContext>
        </Stack>
      </Box>

      <DashboardSettingsDialog
        open={settingsOpen}
        monitorings={settingCards}
        hasPressReleaseMonitoring={hasPressReleaseMonitoring}
        draft={currentSettingDraft}
        saving={savingSettings}
        errorMessage={settingsError}
        onClose={closeSettingsDialog}
        onSave={() => saveDashboardSetting(currentSettingDraft)}
        onDraftChange={(draft) => setSettingsDraft(draft)}
      />
    </Box>
  );
};

const SortableDashboardItem = ({
  id,
  active,
  children,
}: {
  id: string;
  active: boolean;
  children: (dragHandleProps: SortableDragHandleProps) => ReactNode;
}) => {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box ref={setNodeRef} style={style} sx={dragItemSx(active || isDragging)}>
      {children({ attributes, listeners, setActivatorNodeRef })}
    </Box>
  );
};

const SortableDragHandle = ({ dragHandleProps }: { dragHandleProps: SortableDragHandleProps }) => (
  <Box
    component="span"
    ref={dragHandleProps.setActivatorNodeRef}
    {...dragHandleProps.attributes}
    {...dragHandleProps.listeners}
    aria-label="카드 순서 변경"
    sx={dragHandleButtonSx}
  >
    <DragIndicatorIcon sx={dragHandleIconSx} />
  </Box>
);

const ArticlePreviewRow = ({ article, color }: { article: LatestArticle; color: DashboardColor }) => {
  const clickable = Boolean(article.url);
  const openArticle = () => {
    if (article.url) window.open(article.url, "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      role={clickable ? "link" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={openArticle}
      onKeyDown={(event) => {
        if (clickable && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          openArticle();
        }
      }}
      sx={articleRowSx(color, clickable)}
    >
      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flexWrap: "wrap", rowGap: 0.5 }}>
          <Chip label={article.searchTerm} size="small" sx={searchTermChipSx} />
          <Typography sx={articleMetaSx}>{article.publisher} · {article.reporter} · {article.collectedAt}</Typography>
        </Stack>
        {clickable && (
          <Tooltip title="원문 열기" arrow>
            <OpenInNewIcon sx={articleExternalIconSx} />
          </Tooltip>
        )}
      </Stack>
      <Typography sx={articleTitleSx}>{article.title}</Typography>
    </Box>
  );
};

const MonitoringCard = ({
  monitoring,
  color,
  latestArticleLimit,
  articleRangeLabel,
  dragHandleProps,
  onDetail,
}: {
  monitoring: MonitoringCardData;
  color: DashboardColor;
  latestArticleLimit: number;
  articleRangeLabel: string;
  dragHandleProps: SortableDragHandleProps;
  onDetail: () => void;
}) => {
  const visibleArticles = monitoring.articles.slice(0, latestArticleLimit);
  const cardArticleCount = monitoring.articleError ? 0 : visibleArticles.length;

  return (
    <Paper variant="outlined" sx={monitoringCardSx(getMonitoringCardHeight(cardArticleCount))}>
      <Stack direction="row" justifyContent="space-between" spacing={0.8} alignItems="center" sx={cardHeaderSx}>
        <Stack direction="row" spacing={1.5} sx={{ minWidth: 0 }} alignItems="center">
          <SortableDragHandle dragHandleProps={dragHandleProps} />
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} sx={{ minWidth: 0, overflow: "hidden" }} alignItems="center">
              <Typography sx={cardTitleSx} noWrap>{monitoring.title}</Typography>
              <Typography sx={articleCountSx(color)}>{monitoring.latestCount}건</Typography>
            </Stack>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ flexShrink: 0 }}>
          <Button size="small" variant="outlined" endIcon={<DashboardIcon src={dashboardIcons.detailMore} sx={detailButtonIconSx} />} sx={inlineDetailButtonSx} onClick={onDetail}>
            상세보기
          </Button>
        </Stack>
      </Stack>

      <Stack spacing={1} sx={cardBodySx(cardArticleCount)}>
        {monitoring.articleError ? (
          <Box sx={errorArticleSx}>
            <Typography sx={errorArticleTextSx}>{monitoring.articleError}</Typography>
          </Box>
        ) : visibleArticles.length > 0 ? visibleArticles.map((article) => (
          <ArticlePreviewRow key={article.id} article={article} color={color} />
        )) : (
          <Box sx={emptyArticleSx}>
            <Typography sx={{ color: "#6F7279", fontSize: 14, fontWeight: 500, lineHeight: "20px" }}>
              표시 조건에 맞는 {articleRangeLabel} 기사가 없습니다.
            </Typography>
          </Box>
        )}
      </Stack>

      <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={cardFooterSx}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={footerMetaSx}>
          <Typography sx={footerTextSx}>{monitoring.interval}</Typography>
          <Box sx={footerDividerSx} />
          <Stack direction="row" spacing={0.5} alignItems="center" sx={footerStatusSx}>
            <StatusDot status={monitoring.status} />
            <Typography sx={footerTextSx}>{monitoring.status === "ACTIVE" ? "정상 수집" : "비활성화"}</Typography>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
};

const PressReleaseAggregateCard = ({
  color,
  summary,
  errorMessage,
  dragHandleProps,
  onDetail,
}: {
  color: DashboardColor;
  summary: PressReleaseDashboardSummary;
  errorMessage?: string | null;
  dragHandleProps: SortableDragHandleProps;
  onDetail: () => void;
}) => {
  const pressAccentColor = color === "none" ? "blue" : color;
  const pressSummaryRowCount = errorMessage ? 1 : Math.max(1, summary.pressReleaseSummaries.length);

  return (
    <Paper variant="outlined" sx={monitoringCardSx(getPressReleaseCardHeight(pressSummaryRowCount))}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={cardHeaderSx}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <SortableDragHandle dragHandleProps={dragHandleProps} />
          <Box>
            <Typography sx={cardTitleSx}>보도자료 모니터링</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.6} alignItems="center">
          <Button size="small" variant="outlined" endIcon={<DashboardIcon src={dashboardIcons.detailMore} sx={detailButtonIconSx} />} sx={inlineDetailButtonSx} onClick={onDetail}>
            상세보기
          </Button>
        </Stack>
      </Stack>

      <Box sx={pressSummaryWrapSx}>
        <Box sx={pressSummarySx}>
          <Box sx={pressSummaryPrimarySx}>
            <Typography sx={pressSummaryLabelSx}>오늘 배포</Typography>
            <Box sx={pressSummaryValueWrapSx}>
              <Typography component="span" sx={pressSummaryValueSx}>보도자료</Typography>
              <Typography component="span" sx={pressSummaryCountStrongSx}>{summary.releasedPressCount}건</Typography>
            </Box>
          </Box>
          <Box sx={pressSummarySecondarySx}>
            <Typography sx={pressSummaryLabelSx}>연관 수집 기사</Typography>
            <Box sx={{ textAlign: "right" }} title={`수정 감지 기사 ${summary.revisionDetectedArticleCount}건`}>
              <Typography sx={pressSummaryCountStrongSx}>{summary.relatedArticleCount}건</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Stack spacing={0.75} sx={pressListSx(pressSummaryRowCount)}>
        {errorMessage ? (
          <Box sx={errorArticleSx}>
            <Typography sx={errorArticleTextSx}>보도자료 요약 조회 실패: {errorMessage}</Typography>
          </Box>
        ) : summary.pressReleaseSummaries.length > 0 ? summary.pressReleaseSummaries.map((item) => (
          <Box key={item.key} sx={pressRowSx}>
            <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography sx={pressTitleSx}>{item.title}</Typography>
              <Typography sx={articleCountSx(pressAccentColor)}>{item.count}건</Typography>
            </Stack>
          </Box>
        )) : (
          <Box sx={emptyArticleSx}>
            <Typography sx={{ color: "#6F7279", fontSize: 14, fontWeight: 500, lineHeight: "20px" }}>조회된 보도자료 집계가 없습니다.</Typography>
          </Box>
        )}
      </Stack>

      <Box sx={cardFooterSx}>
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
          <Typography sx={pressFooterTextSx}>익일 오전 9시까지 집계</Typography>
        </Stack>
      </Box>
    </Paper>
  );
};

const DashboardSettingsDialog = ({
  open,
  monitorings,
  hasPressReleaseMonitoring,
  draft,
  saving,
  errorMessage,
  onClose,
  onSave,
  onDraftChange,
}: {
  open: boolean;
  monitorings: MonitoringCardData[];
  hasPressReleaseMonitoring: boolean;
  draft: DashboardSettingDraft;
  saving: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onSave: () => void;
  onDraftChange: (draft: DashboardSettingDraft) => void;
}) => {
  const [settingSearch, setSettingSearch] = useState("");

  const updateDraft = (updater: (prev: DashboardSettingDraft) => DashboardSettingDraft) => {
    onDraftChange(updater(buildDashboardSettingDraft(draft)));
  };

  const toggleItem = (id: string) => {
    updateDraft((prev) => ({
      ...prev,
      visibleItemIds: prev.visibleItemIds.includes(id)
        ? prev.visibleItemIds.filter((item) => item !== id)
        : [...prev.visibleItemIds, id],
    }));
  };

  const updateCardColor = (id: string, color: DashboardColor) => {
    updateDraft((prev) => ({
      ...prev,
      cardColors: {
        ...prev.cardColors,
        [id]: id === pressReleaseItemId ? "none" : color,
      },
    }));
  };

  const updateArticleLimit = (id: string, value: number) => {
    updateDraft((prev) => ({
      ...prev,
      articleLimits: {
        ...prev.articleLimits,
        [id]: value,
      },
    }));
  };

  const updateArticleDisplayFilter = (id: string, nextFilter: ArticleDisplayFilter) => {
    updateDraft((prev) => {
      const sanitized = sanitizeArticleDisplayFilter(nextFilter);
      const nextFilters = cloneArticleDisplayFilters(prev.articleDisplayFilters);
      if (sanitized.negativeOnly || sanitized.revisionOnly) {
        nextFilters[id] = sanitized;
      } else {
        delete nextFilters[id];
      }
      return { ...prev, articleDisplayFilters: nextFilters };
    });
  };

  const settingItems = [
    ...monitorings.map((item) => ({
      id: item.id,
      title: item.title,
      desc: `${item.interval} · 표시 기사 ${item.latestCount}건`,
      sentimentEnabled: item.sentimentEnabled === true,
      revisionTrackingEnabled: item.revisionTrackingEnabled === true,
    })),
    ...(hasPressReleaseMonitoring
      ? [{ id: pressReleaseItemId, title: "보도자료 모니터링", desc: "오늘 배포 보도자료와 연관 기사 집계", sentimentEnabled: false, revisionTrackingEnabled: true }]
      : []),
  ];
  const normalizedSettingSearch = settingSearch.trim().toLowerCase();
  const filteredSettingItems = normalizedSettingSearch
    ? settingItems.filter((item) => `${item.title} ${item.desc}`.toLowerCase().includes(normalizedSettingSearch))
    : settingItems;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: settingsDialogPaperSx }}
      BackdropProps={{ sx: settingsDialogBackdropSx }}
    >
      <DialogTitle sx={dialogTitleSx}>
        <Stack sx={settingsTitleStackSx}>
          <Typography sx={settingsDialogTitleTextSx}>표시 설정</Typography>
          <Typography sx={settingsDialogSubtitleSx}>
            모니터링 항목의 최신 기사 표시 개수, 기사 박스 색상, 기사 표시 기준을 조정합니다.
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={settingsDialogContentSx}>
        <Stack sx={settingsBodyStackSx}>
          {errorMessage && (
            <Alert severity="error" sx={settingsErrorAlertSx}>
              {errorMessage}
            </Alert>
          )}
          <Box sx={settingSearchGroupSx}>
            <TextField
              size="small"
              placeholder="모니터링명 검색"
              value={settingSearch}
              onChange={(event) => setSettingSearch(event.target.value)}
              sx={settingSearchFieldSx}
              inputProps={{ "aria-label": "모니터링명 검색" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DashboardIcon src={dashboardIcons.settingsSearch} sx={settingSearchIconSx} />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={hintBoxSx}>
              <DashboardIcon src={dashboardIcons.settingsInfo} sx={hintIconSx} />
              <Typography sx={hintTextSx}>
                원하는 모니터링 항목이 보이지 않으신다면 '모니터링 관리' 페이지에서 대시보드 표시 토글이 활성화되어 있는지 확인해 주세요.
              </Typography>
            </Box>
          </Box>
          <Stack data-testid="display-settings-card-list" sx={settingItemsStackSx}>
            {filteredSettingItems.map((item) => {
              const displayFilter = draft.articleDisplayFilters[item.id] ?? {};
              const selectedColor = item.id === pressReleaseItemId ? "none" : (draft.cardColors[item.id] ?? "blue");
              return (
                <Box key={item.id} sx={settingItemSx}>
                  <Stack direction="row" alignItems="center" spacing={0.9} sx={settingItemHeaderSx}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={draft.visibleItemIds.includes(item.id)}
                          onChange={() => toggleItem(item.id)}
                          size="small"
                          sx={settingCheckboxSx}
                        />
                      }
                      label={
                        <Box sx={settingLabelTextSx}>
                          <Typography sx={settingItemTitleSx}>{item.title}</Typography>
                          <Typography sx={settingItemDescSx}>{item.desc}</Typography>
                        </Box>
                      }
                      sx={settingLabelSx}
                    />
                  </Stack>
                  <Box sx={settingRowSx}>
                    {item.id !== pressReleaseItemId ? (
                      <Box>
                        <Typography sx={settingControlLabelSx}>기사 표시 개수</Typography>
                        <TextField
                          select
                          size="small"
                          inputProps={{ "aria-label": `${item.title} 기사 표시 개수` }}
                          SelectProps={{ IconComponent: SettingsSelectIcon }}
                          value={draft.articleLimits[item.id] ?? 3}
                          onChange={(event) => updateArticleLimit(item.id, Number(event.target.value))}
                          sx={articleLimitFieldSx}
                        >
                          {[1, 2, 3].map((count) => (
                            <MenuItem key={count} value={count}>{count}개</MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    ) : (
                      <Box>
                        <Typography sx={settingControlLabelSx}>기사 표시 개수</Typography>
                        <TextField
                          select
                          size="small"
                          disabled
                          value={1}
                          inputProps={{ "aria-label": `${item.title} 기사 표시 개수` }}
                          SelectProps={{ IconComponent: SettingsSelectIcon }}
                          sx={fixedArticleLimitFieldSx}
                        >
                          <MenuItem value={1}>1개</MenuItem>
                        </TextField>
                      </Box>
                    )}
                    <Box>
                      <Typography sx={settingControlLabelSx}>표시 색</Typography>
                      {item.id !== pressReleaseItemId ? (
                        <Stack direction="row" spacing={0} alignItems="center" sx={settingColorGroupSx}>
                          {dashboardColors.map((color) => {
                            const selected = selectedColor === color;
                            return (
                              <Box
                                key={color}
                                component="button"
                                type="button"
                                aria-label={`${item.title} ${getDashboardColorLabel(color)} 색상`}
                                aria-pressed={selected}
                                onClick={() => updateCardColor(item.id, color)}
                                sx={colorButtonSx(color, selected)}
                              >
                                {color === "none" && !selected && <Box component="span" sx={noneSlashSx} />}
                                {selected && <CheckIcon sx={colorCheckIconSx} />}
                              </Box>
                            );
                          })}
                        </Stack>
                      ) : (
                        <Box sx={fixedColorSx}>기본색 고정</Box>
                      )}
                    </Box>
                    {(item.sentimentEnabled || item.revisionTrackingEnabled) && (
                      <Box sx={settingFilterSx}>
                        <Typography sx={settingControlLabelSx}>표시 기준</Typography>
                        <TextField
                          select
                          size="small"
                          inputProps={{ "aria-label": `${item.title} 표시 기준` }}
                          SelectProps={{ IconComponent: SettingsSelectIcon }}
                          value={getArticleDisplayFilterMode(displayFilter)}
                          onChange={(event) => updateArticleDisplayFilter(item.id, getArticleDisplayFilterFromMode(event.target.value as ArticleDisplayFilterMode))}
                          sx={articleFilterFieldSx}
                        >
                          {getArticleDisplayFilterOptions(item.sentimentEnabled, item.revisionTrackingEnabled).map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
            {filteredSettingItems.length === 0 && (
              <Box sx={settingEmptySx}>
                <Typography sx={{ color: "#667085", fontSize: 12, fontWeight: 400, lineHeight: "20px", fontFamily: dashboardFontFamily, letterSpacing: 0 }}>
                  검색 조건에 맞는 모니터링이 없습니다.
                </Typography>
              </Box>
            )}
          </Stack>

        </Stack>
      </DialogContent>
      <DialogActions sx={settingsDialogActionsSx}>
        <Button variant="outlined" sx={settingsCancelButtonSx} onClick={onClose} disabled={saving}>취소</Button>
        <Button variant="contained" sx={settingsSaveButtonSx} onClick={onSave} disabled={saving}>
          {saving ? "저장 중" : "설정 저장"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DashboardIcon = ({ src, sx = {} }: { src: string; sx?: Record<string, unknown> }) => (
  <Box component="img" src={src} alt="" aria-hidden="true" draggable={false} sx={{ display: "block", objectFit: "contain", flexShrink: 0, ...sx }} />
);

const SettingsSelectIcon = ({ className }: { className?: string }) => (
  <Box
    component="img"
    className={className}
    src={dashboardIcons.settingsDropdown}
    alt=""
    aria-hidden="true"
    draggable={false}
    sx={selectArrowIconSx}
  />
);

const MetricCard = ({ icon, label, value, caption, tone }: { icon: ReactNode; label: string; value: string; caption: string; tone: Tone }) => (
  <Paper variant="outlined" sx={metricCardSx}>
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Box sx={{ ...metricIconSx, bgcolor: toneBg[tone] }}>
        {icon}
      </Box>
      <Stack spacing={1} sx={{ minWidth: 0, height: 81 }}>
        <Typography sx={metricLabelSx}>{label}</Typography>
        <Stack spacing={0.125} sx={{ minWidth: 0 }}>
          <Typography sx={metricValueSx}>{value}</Typography>
          <Typography sx={metricCaptionSx}>{caption}</Typography>
        </Stack>
      </Stack>
    </Stack>
  </Paper>
);

const StatusDot = ({ status }: { status: "ACTIVE" | "PAUSED" }) => {
  const active = status === "ACTIVE";
  return <Box sx={statusDotSx(active)} />;
};

const toneBg: Record<Tone, string> = {
  red: "#FFE3E2",
  amber: "#FFF3DB",
  green: "#e4f8ee",
  blue: "#E2EDFF",
  teal: "#e0f7fa",
  violet: "#EDE9FE",
  gray: "#f1f5f9",
};

const dashboardColors: DashboardColor[] = ["none", "red", "green", "blue", "yellow"];

const dashboardColorBg: Record<DashboardColor, string> = {
  none: "#fcfcfd",
  red: "#FCEEEE",
  green: "#f0fdf4",
  blue: "#E0F2FE",
  yellow: "#fffbeb",
};

const dashboardColorStrong: Record<DashboardColor, string> = {
  none: "#98a2b3",
  red: "#DC2626",
  green: "#16a34a",
  blue: "#0369A1",
  yellow: "#d97706",
};

const dashboardColorBorder: Record<DashboardColor, string> = {
  none: "#DDE0EA",
  red: "#F8BCBC",
  green: "#BBF7D0",
  blue: "#BAE6FD",
  yellow: "#FDE68A",
};

const getDashboardColorLabel = (color: DashboardColor) => {
  if (color === "none") return "없음";
  return color;
};

const getArticleDisplayFilterMode = (filter: ArticleDisplayFilter = {}): ArticleDisplayFilterMode => {
  if (filter.negativeOnly && filter.revisionOnly) return "NEGATIVE_OR_REVISION";
  if (filter.negativeOnly) return "NEGATIVE";
  if (filter.revisionOnly) return "REVISION";
  return "ALL";
};

const getArticleDisplayFilterFromMode = (mode: ArticleDisplayFilterMode): ArticleDisplayFilter => {
  if (mode === "NEGATIVE") return { negativeOnly: true };
  if (mode === "REVISION") return { revisionOnly: true };
  if (mode === "NEGATIVE_OR_REVISION") return { negativeOnly: true, revisionOnly: true };
  return {};
};

const getArticleDisplayFilterOptions = (sentimentEnabled: boolean, revisionTrackingEnabled: boolean) => {
  const options = [{ value: "ALL" as ArticleDisplayFilterMode, label: "전체 기사 표시" }];
  if (sentimentEnabled) options.push({ value: "NEGATIVE", label: "부정 기사만 표시" });
  if (revisionTrackingEnabled) options.push({ value: "REVISION", label: "수정 감지 기사만 표시" });
  if (sentimentEnabled && revisionTrackingEnabled) {
    options.push({ value: "NEGATIVE_OR_REVISION", label: "부정 또는 수정 감지 기사 모두 표시" });
  }
  return options;
};

const dashboardFontFamily = "\"Pretendard\", sans-serif";

const pageSx = { minHeight: "100%", bgcolor: "#F2F4F8", px: { xs: 2, md: 3 }, py: { xs: 2.5, md: 5 }, fontFamily: dashboardFontFamily, letterSpacing: 0 };
const pageContentSx = { width: "100%", maxWidth: 1518, mx: "auto" };
const dashboardHeaderSx = { alignItems: { xs: "stretch", md: "flex-start" }, minHeight: { md: 60 }, mb: { xs: 2, md: 5 } };
const titleRowSx = { flexWrap: "wrap", rowGap: 0.75 };
const pageTitleSx = { color: "#2E3341", fontSize: { xs: 22, md: 24 }, fontWeight: 700, lineHeight: "32px" };
const dashboardDateBadgeSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 24,
  width: 128,
  flex: "0 0 128px",
  boxSizing: "border-box",
  borderRadius: "16px",
  bgcolor: "#90939B",
  color: "#fff",
  px: 1,
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "13px",
  whiteSpace: "nowrap",
};
const pageSubtitleSx = { mt: 0.5, color: "#2E3341", fontSize: 16, fontWeight: 400, lineHeight: "24px" };
const headerActionsSx = { flexWrap: "wrap", justifyContent: { xs: "flex-start", md: "flex-end" }, rowGap: 1, pt: { xs: 0, md: 1.5 } };
const headerOutlineButtonSx = {
  height: 40,
  minHeight: 40,
  minWidth: "auto",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.5,
  flexShrink: 0,
  boxSizing: "border-box",
  borderRadius: "8px",
  borderColor: "#DDE0EA",
  color: "#2E3341",
  bgcolor: "#fff",
  px: 2,
  py: 0,
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  whiteSpace: "nowrap",
  wordBreak: "keep-all",
  boxShadow: "none",
  "&:hover": { borderColor: "#C9CDD8", bgcolor: "#fff", boxShadow: "none" },
  "& .MuiButton-startIcon": { m: 0, height: 16, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
};
const headerActionIconSx = (variant: "refresh" | "settings" | "add") => ({
  width: variant === "refresh" ? 14 : 16,
  height: variant === "refresh" ? 15 : 16,
});
const headerButtonTextSx = { height: 14, display: "inline-flex", alignItems: "center", lineHeight: "14px" };
const headerRefreshButtonSx = {};
const headerSettingsButtonSx = {};
const headerPrimaryButtonSx = {
  height: 40,
  minHeight: 40,
  minWidth: "auto",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.5,
  flexShrink: 0,
  boxSizing: "border-box",
  borderRadius: "8px",
  bgcolor: "#2E3341",
  color: "#fff",
  px: 2,
  py: 0,
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  whiteSpace: "nowrap",
  wordBreak: "keep-all",
  boxShadow: "none",
  "&:hover": { bgcolor: "#252A36", boxShadow: "none" },
  "& .MuiButton-startIcon": { m: 0, height: 16, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
};
const rangePanelSx = {
  height: 72,
  minHeight: 72,
  borderRadius: "12px",
  bgcolor: "#fff",
  border: "none",
  boxSizing: "border-box",
  boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)",
  display: "flex",
  alignItems: "center",
  gap: { xs: 1.25, sm: 2 },
  px: { xs: 2, md: 3 },
  py: { xs: 1.5, md: 2 },
  flexWrap: "wrap",
  mb: 2,
};
const rangePanelLabelSx = { color: "#2E3341", fontSize: 13, fontWeight: 600, lineHeight: "20px", whiteSpace: "nowrap" };
const rangeRadioGroupSx = { gap: { xs: 1.5, sm: 3 }, flexWrap: "wrap" };
const rangeRadioOptionSx = {
  m: 0,
  height: 40,
  gap: 1,
  "& .MuiFormControlLabel-label": {
    color: "#2E3341",
    fontSize: 16,
    fontWeight: 500,
    lineHeight: "24px",
  },
};
const rangeRadioSx = {
  p: 0,
};
const rangeRadioIconSx = {
  width: 16,
  height: 16,
  borderRadius: "50%",
  bgcolor: "#E5E5E5",
};
const rangeRadioCheckedIconSx = {
  width: 16,
  height: 16,
  borderRadius: "50%",
  bgcolor: "#5D88F1",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&::after": {
    content: "\"\"",
    width: 8,
    height: 8,
    borderRadius: "50%",
    bgcolor: "#fff",
  },
};
const partialErrorAlertSx = { borderRadius: "8px", py: 0.5, "& .MuiAlert-message": { color: "#92400e", fontSize: 12, fontWeight: 500 } };
const partialErrorsStackSx = { mb: 2 };
const settingsErrorAlertSx = { borderRadius: "8px", py: 0.5, "& .MuiAlert-message": { fontSize: 12, fontWeight: 500 } };
const panelSx = { overflow: "hidden", borderRadius: "12px", border: "none", bgcolor: "#fff", boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)" };
const refreshingNoticeSx = { ...panelSx, position: "relative", px: 1.5, py: 1, mb: 2, display: "flex", alignItems: "center", gap: 1 };
const refreshingProgressSx = {
  position: "absolute",
  left: 0,
  right: 0,
  top: 0,
  height: 2,
  bgcolor: "#e0f2fe",
  "& .MuiLinearProgress-bar": { bgcolor: "#0891b2" },
};
const refreshingTextSx = { color: "#475467", fontSize: 12, fontWeight: 500 };
const summaryGridSx = { display: "grid", gridTemplateColumns: { xs: "repeat(1, minmax(0, 1fr))", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 2, mb: 2 };
const dashboardGridSx = {
  columnCount: { xs: 1, lg: 2 },
  columnGap: 2,
};
const getCardBodyHeight = (articleCount: number) => (articleCount > 0 ? 48 + articleCount * 88 + Math.max(0, articleCount - 1) * 8 : 100);
const getMonitoringCardHeight = (articleCount: number) => 64 + getCardBodyHeight(articleCount) + 44;
const getPressListHeight = (rowCount: number) => (rowCount * 52) + Math.max(0, rowCount - 1) * 6 + 24;
const getPressReleaseCardHeight = (rowCount: number) => 64 + 112 + getPressListHeight(rowCount) + 44;
const monitoringCardSx = (height: number) => ({
  ...panelSx,
  height,
  borderRadius: "12px",
  boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
});
const cardHeaderSx = { px: { xs: 2, md: 3 }, pt: 3, pb: 1, height: 64, minHeight: 64, flex: "0 0 64px", boxSizing: "border-box" };
const cardBodySx = (articleCount: number) => ({
  px: { xs: 2, md: 3 },
  py: 3,
  height: getCardBodyHeight(articleCount),
  flex: `0 0 ${getCardBodyHeight(articleCount)}px`,
  boxSizing: "border-box",
  overflow: "hidden",
});
const cardFooterSx = { px: 2, py: 1.5, borderTop: "1px solid #DDE0EA", height: 44, minHeight: 44, flex: "0 0 44px", boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "flex-end", overflow: "hidden" };
const cardTitleSx = { minWidth: 0, color: "#000000", fontSize: 16, fontWeight: 600, lineHeight: "22px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const articleCountSx = (color: DashboardColor) => ({ display: "inline-flex", alignItems: "center", justifyContent: "center", color: dashboardColorStrong[color], fontSize: 13, fontWeight: 500, lineHeight: "13px", whiteSpace: "nowrap", flexShrink: 0, borderRadius: "16px", px: 1, height: 24, bgcolor: dashboardColorBg[color] });
const articleRowSx = (color: DashboardColor, clickable = false) => ({
  height: 88,
  minHeight: 88,
  border: `1px solid ${dashboardColorBorder[color]}`,
  borderRadius: "8px",
  bgcolor: color === "none" ? "#FAFAFA" : dashboardColorBg[color],
  boxSizing: "border-box",
  boxShadow: "none",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  gap: 1,
  p: 2,
  overflow: "hidden",
  cursor: clickable ? "pointer" : "default",
  transition: "border-color .15s ease, box-shadow .15s ease, transform .15s ease",
  "&:hover": clickable
    ? { borderColor: color === "none" ? "#C9CDD8" : dashboardColorBorder[color], boxShadow: "0 4px 12px rgba(68,100,178,.08)", transform: "translateY(-1px)" }
    : {},
});
const emptyArticleSx = { border: "1px dashed #DDE0EA", borderRadius: "8px", bgcolor: "#FAFAFA", px: 2, py: 2, height: 52, minHeight: 52, boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", overflow: "hidden" };
const errorArticleSx = { border: "1px solid #fbbf24", borderRadius: "8px", bgcolor: "#fffbeb", px: 1.5, py: 1.25, height: 52, boxSizing: "border-box", overflow: "hidden" };
const errorArticleTextSx = { color: "#92400e", fontSize: 13, fontWeight: 500, lineHeight: "20px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const articleTitleSx = { color: "#000000", fontSize: 16, fontWeight: 500, lineHeight: "24px", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const articleMetaSx = { color: "#6F7279", fontSize: 13, fontWeight: 500, lineHeight: "20px", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const articleExternalIconSx = { flexShrink: 0, color: "#9AA4B4", fontSize: 16, width: 16, height: 16 };
const searchTermChipSx = {
  height: 24,
  borderRadius: "16px",
  bgcolor: "#E0F2FE",
  color: "#0369A1",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "13px",
  maxWidth: 140,
  flexShrink: 0,
  px: 0,
  "& .MuiChip-label": { px: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
};
const metricCardSx = {
  height: 113,
  minHeight: 113,
  px: 2,
  py: 2,
  borderRadius: "12px",
  border: "none",
  boxSizing: "border-box",
  bgcolor: "#fff",
  boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)",
  overflow: "hidden",
};
const metricIconSx = { width: 40, height: 40, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };
const metricIconImageSx = (tone: Tone) => ({
  width: tone === "blue" ? 24 : tone === "violet" ? 27 : tone === "amber" ? 26 : 27,
  height: tone === "blue" ? 25 : tone === "violet" ? 23 : tone === "amber" ? 25 : 27,
});
const metricLabelSx = { color: "#2E3341", fontSize: 14, fontWeight: 600, lineHeight: "20px" };
const metricValueSx = { color: "#2E3341", fontSize: 24, fontWeight: 700, lineHeight: "32px" };
const metricCaptionSx = { color: "#6F7279", fontSize: 14, fontWeight: 500, lineHeight: "20px" };
const pressSummaryWrapSx = { px: { xs: 2, md: 3 }, pt: 3, pb: 1, height: 112, boxSizing: "border-box", overflow: "hidden" };
const pressListSx = (rowCount: number) => ({ px: { xs: 2, md: 3 }, pb: 3, height: getPressListHeight(rowCount), boxSizing: "border-box", overflow: "hidden" });
const pressRowSx = { height: 52, minHeight: 52, border: "1px solid #DDE0EA", borderRadius: "8px", bgcolor: "#FAFAFA", px: 2, py: 1.5, boxSizing: "border-box", overflow: "hidden" };
const pressTitleSx = { color: "#000000", fontSize: 16, fontWeight: 500, lineHeight: "24px", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const pressSummarySx = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1, height: 80 };
const pressSummaryPrimarySx = { borderRadius: "8px", px: 3, py: 3, height: 80, minHeight: 80, boxSizing: "border-box", bgcolor: "#5D88F1", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, overflow: "hidden" };
const pressSummarySecondarySx = { borderRadius: "8px", px: 3, py: 3, height: 80, minHeight: 80, boxSizing: "border-box", bgcolor: "#F0F4FF", color: "#2E3341", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, overflow: "hidden" };
const pressSummaryLabelSx = { color: "inherit", opacity: 1, fontSize: 14, fontWeight: 600, lineHeight: "20px", whiteSpace: "nowrap" };
const pressSummaryValueWrapSx = { display: "inline-flex", alignItems: "baseline", gap: 1, whiteSpace: "nowrap" };
const pressSummaryValueSx = { color: "inherit", fontSize: 14, fontWeight: 500, lineHeight: "20px" };
const pressSummaryCountStrongSx = { color: "inherit", fontSize: 24, fontWeight: 700, lineHeight: "32px" };
const footerTextSx = { color: "#6F7279", fontSize: 14, fontWeight: 500, lineHeight: "20px", whiteSpace: "nowrap", wordBreak: "keep-all" };
const pressFooterTextSx = { ...footerTextSx, width: 128, height: 20, textAlign: "right" };
const footerMetaSx = { height: 20, minWidth: 0, flexShrink: 0, flexWrap: "nowrap", whiteSpace: "nowrap" };
const footerStatusSx = { height: 20, minWidth: 0, flexShrink: 0, flexWrap: "nowrap", whiteSpace: "nowrap" };
const footerDividerSx = { width: 0, height: 14, borderLeft: "1px solid #DDE0EA", flex: "0 0 0px" };
const statusDotSx = (active: boolean) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  bgcolor: active ? "#4BC559" : "#94a3b8",
  flexShrink: 0,
  animation: "none",
});
const emptySx = { ...panelSx, p: 3, textAlign: "center" };
const dragHandleButtonSx = {
  width: 16,
  height: 24,
  borderRadius: "6px",
  color: "#94a3b8",
  cursor: "grab",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "0 0 auto",
  touchAction: "none",
  outline: "none",
  "&:hover": { bgcolor: "#f1f5f9", color: "#475569" },
  "&:focus-visible": { boxShadow: "0 0 0 3px rgba(8,145,178,.18)" },
  "&:active": { cursor: "grabbing" },
};
const dragHandleIconSx = { fontSize: 18 };
const dragItemSx = (active: boolean) => ({
  display: "inline-block",
  width: "100%",
  minWidth: 0,
  mb: 2,
  breakInside: "avoid",
  WebkitColumnBreakInside: "avoid",
  pageBreakInside: "avoid",
  opacity: active ? 0.72 : 1,
  position: "relative",
  zIndex: active ? 10 : "auto",
  transition: "opacity .15s ease, box-shadow .15s ease",
  willChange: "transform",
});
const settingsDialogBackdropSx = { bgcolor: "rgba(0, 0, 0, .4)" };
const settingsDialogPaperSx = {
  width: { xs: "calc(100% - 32px)", md: 780 },
  height: { xs: "calc(100% - 32px)", md: 902 },
  maxWidth: "calc(100% - 32px)",
  maxHeight: "calc(100% - 32px)",
  minHeight: 0,
  borderRadius: "24px",
  boxShadow: "0px 6px 14px rgba(68, 100, 178, 0.08)",
  fontFamily: dashboardFontFamily,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};
const settingsTitleSearchGap = "24px";
const dialogTitleSx = { display: "flex", alignItems: "flex-start", justifyContent: "flex-start", width: "100%", height: { xs: "auto", md: 90 }, minHeight: { md: 90 }, flex: "0 0 auto", flexShrink: 0, boxSizing: "border-box", gap: "7px", px: { xs: 2.5, md: 5 }, pt: { xs: 3, md: "40px" }, pb: 0 };
const settingsTitleStackSx = { width: { xs: "100%", md: 700 }, height: { xs: "auto", md: 50 }, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", gap: "4px" };
const settingsDialogTitleTextSx = { color: "#000000", fontSize: 20, fontWeight: 600, lineHeight: "26px", fontFamily: dashboardFontFamily, letterSpacing: 0 };
const settingsDialogSubtitleSx = { color: "#2E3341", fontSize: 13, fontWeight: 400, lineHeight: "20px", fontFamily: dashboardFontFamily, letterSpacing: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "clip", maxWidth: "100%" };
const settingsDialogContentSx = { width: "100%", minHeight: 0, flex: "1 1 auto", display: "flex", boxSizing: "border-box", px: { xs: 2.5, md: "40px" }, pt: settingsTitleSearchGap, pb: { xs: 3, md: "24px" }, overflow: "hidden", "&&&": { pt: settingsTitleSearchGap } };
const settingsBodyStackSx = { width: "100%", height: "100%", minHeight: 0, flex: "1 1 auto", gap: "24px" };
const settingSearchGroupSx = { width: "100%", height: { xs: "auto", md: 92 }, display: "flex", flexDirection: "column", gap: "8px", flexShrink: 0 };
const settingItemsStackSx = {
  width: "100%",
  height: "auto",
  minHeight: 0,
  flex: "1 1 auto",
  gap: { xs: 3, md: "40px" },
  overflowY: "auto",
  overflowX: "hidden",
  pr: 0,
  scrollbarColor: "#B8C0CC transparent",
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-track": { background: "transparent", border: "none", boxShadow: "none" },
  "&::-webkit-scrollbar-thumb": { backgroundColor: "#B8C0CC", borderRadius: "999px", border: "none" },
  "&::-webkit-scrollbar-button": { display: "none", width: 0, height: 0 },
  "&::-webkit-scrollbar-corner": { background: "transparent" },
};
const settingItemSx = { width: "100%", height: { xs: "auto", md: 152 }, overflow: "visible", flex: { md: "0 0 152px" }, display: "flex", flexDirection: "column" };
const settingItemHeaderSx = { height: 40, minHeight: 40, boxSizing: "border-box", py: 1, mb: 0, minWidth: 0, borderRadius: "8px" };
const settingLabelTextSx = { minWidth: 0, display: "flex", alignItems: "center", gap: "8px", flexWrap: "nowrap", height: 24 };
const settingItemTitleSx = { color: "#000000", fontSize: 16, fontWeight: 600, lineHeight: "24px", fontFamily: dashboardFontFamily, letterSpacing: 0, whiteSpace: "nowrap", flexShrink: 0 };
const settingItemDescSx = { color: "#6F7279", fontSize: 13, fontWeight: 500, lineHeight: "20px", fontFamily: dashboardFontFamily, letterSpacing: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 };
const settingRowSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "120px 200px minmax(0, 284px)" },
  gap: { xs: 2, md: "24px" },
  alignItems: "flex-start",
  border: "1px solid #DDE0EA",
  borderRadius: "8px",
  bgcolor: "#FAFAFA",
  width: "100%",
  height: { xs: "auto", md: 112 },
  boxSizing: "border-box",
  p: { xs: 2, md: "24px" },
};
const settingFilterSx = { minWidth: 0 };
const settingControlLabelSx = { color: "#2E3341", fontSize: 13, fontWeight: 600, lineHeight: "20px", fontFamily: dashboardFontFamily, letterSpacing: 0, mb: 0.5 };
const articleLimitFieldSx = {
  width: { xs: "100%", md: 120 },
  "& .MuiInputBase-root": { height: 40, borderRadius: "8px", bgcolor: "#fff", fontSize: 14, fontWeight: 400, fontFamily: dashboardFontFamily, lineHeight: "20px" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DDE0EA" },
  "& .MuiSelect-select": { py: 1, px: 2, fontFamily: dashboardFontFamily, fontSize: 14, fontWeight: 400, lineHeight: "20px", letterSpacing: 0 },
  "& .MuiSelect-icon": { color: "#2E3341", right: 12 },
};
const fixedArticleLimitFieldSx = {
  ...articleLimitFieldSx,
  "& .MuiInputBase-root.Mui-disabled": { bgcolor: "#F2F2F2" },
  "& .MuiSelect-select.Mui-disabled": { color: "#6F7279", WebkitTextFillColor: "#6F7279" },
  "& .MuiSelect-icon.Mui-disabled": { color: "#B8C0CC" },
};
const articleFilterFieldSx = {
  width: "100%",
  "& .MuiInputBase-root": { height: 40, borderRadius: "8px", bgcolor: "#fff", fontSize: 14, fontWeight: 400, fontFamily: dashboardFontFamily, lineHeight: "20px" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DDE0EA" },
  "& .MuiSelect-select": { py: 1, px: 2, fontFamily: dashboardFontFamily, fontSize: 14, fontWeight: 400, lineHeight: "20px", letterSpacing: 0 },
  "& .MuiSelect-icon": { color: "#2E3341", right: 12 },
};
const settingLabelSx = { m: 0, alignItems: "center", minWidth: 0, gap: 1, "& .MuiFormControlLabel-label": { minWidth: 0 } };
const settingCheckboxSx = { p: 0, color: "#DDE0EA", "&.Mui-checked": { color: "#2E3341" }, "& .MuiSvgIcon-root": { fontSize: 20 } };
const settingSearchFieldSx = {
  width: "100%",
  "& .MuiInputBase-root": { height: 48, borderRadius: "8px", bgcolor: "#fff", fontSize: 16, fontWeight: 400, fontFamily: dashboardFontFamily, lineHeight: "24px", px: 2 },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#DDE0EA" },
  "& .MuiInputBase-input": { p: 0, color: "#2E3341", height: 24, letterSpacing: 0 },
  "& .MuiInputBase-input::placeholder": { color: "#A3A3A3", opacity: 1 },
  "& .MuiInputAdornment-root": { mr: 1.5 },
};
const settingSearchIconSx = { width: 18, height: 18 };
const settingEmptySx = { border: "1px dashed #DDE0EA", borderRadius: "8px", bgcolor: "#FAFAFA", px: 2, py: 2, textAlign: "center" };
const hintBoxSx = { width: "100%", height: 36, borderRadius: "8px", bgcolor: "#F2F2F2", px: 1.5, py: 1, boxSizing: "border-box", display: "flex", alignItems: "center", gap: 0.5, overflow: "hidden" };
const hintIconSx = { width: 16, height: 16, flex: "0 0 16px" };
const hintTextSx = { color: "#0D67DD", fontSize: 13, fontWeight: 500, lineHeight: "20px", fontFamily: dashboardFontFamily, letterSpacing: 0, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const settingColorGroupSx = { width: { xs: "100%", md: 200 }, height: 40, minHeight: 40, flexWrap: "nowrap", rowGap: 0 };
const fixedColorSx = { width: { xs: "100%", md: 200 }, height: 40, display: "inline-flex", alignItems: "center", justifyContent: "center", textAlign: "center", color: "#6F7279", fontSize: 13, fontWeight: 500, fontFamily: dashboardFontFamily };
const colorButtonSx = (color: DashboardColor, selected: boolean) => ({
  position: "relative",
  minWidth: 40,
  p: 0,
  width: 40,
  height: 40,
  borderRadius: "50%",
  border: "none",
  bgcolor: "transparent",
  cursor: "pointer",
  boxShadow: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "&::before": {
    content: "\"\"",
    position: "absolute",
    left: 4,
    top: 4,
    width: 32,
    height: 32,
    borderRadius: "50%",
    boxSizing: "border-box",
    background: selected
      ? (color === "none" ? "rgba(0, 0, 0, 0.3)" : `linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), ${dashboardColorStrong[color]}`)
      : color === "none" ? "#FFFFFF" : dashboardColorStrong[color],
    border: color === "none" && !selected ? "2px solid #DDE0EA" : "none",
  },
  "&:focus-visible": { outline: "none", boxShadow: "0 0 0 3px rgba(13, 103, 221, .18)" },
});
const colorCheckIconSx = { color: "#fff", fontSize: 18, position: "relative", zIndex: 1 };
const noneSlashSx = {
  position: "absolute",
  left: "50%",
  top: "50%",
  width: 31,
  height: "2px",
  bgcolor: "#DDE0EA",
  transform: "translate(-50%, -50%) rotate(135deg)",
  borderRadius: "999px",
  zIndex: 1,
};
const settingsDialogActionsSx = { width: "100%", height: { xs: "auto", md: 88 }, flex: "0 0 auto", flexShrink: 0, boxSizing: "border-box", justifyContent: "flex-end", alignItems: "flex-start", px: { xs: 2.5, md: 5 }, pb: { xs: 3, md: 5 }, pt: 0, gap: 1.5 };
const selectArrowIconSx = { width: 14, height: 8, right: "16px !important", top: "calc(50% - 4px) !important", pointerEvents: "none" };
const settingsCancelButtonSx = { borderRadius: "8px", borderColor: "#DDE0EA", color: "#2E3341", fontSize: 16, fontWeight: 600, lineHeight: "16px", fontFamily: dashboardFontFamily, minWidth: 68, width: 68, height: 48, bgcolor: "#fff", px: 2.5, boxSizing: "border-box", whiteSpace: "nowrap", wordBreak: "keep-all", flexShrink: 0, boxShadow: "none", "&:hover": { borderColor: "#C9CDD8", bgcolor: "#fff", boxShadow: "none" } };
const settingsSaveButtonSx = { borderRadius: "8px", bgcolor: "#2E3341", color: "#fff", fontSize: 16, fontWeight: 600, lineHeight: "16px", fontFamily: dashboardFontFamily, minWidth: 99, width: 99, height: 48, px: 2.5, boxSizing: "border-box", whiteSpace: "nowrap", wordBreak: "keep-all", flexShrink: 0, boxShadow: "none", "&:hover": { bgcolor: "#252A36", boxShadow: "none" } };
const inlineDetailButtonSx = {
  minWidth: 86,
  width: 86,
  height: 32,
  borderRadius: "8px",
  borderColor: "#DDE0EA",
  color: "#2E3341",
  bgcolor: "#fff",
  p: "0 12px",
  gap: "4px",
  boxSizing: "border-box",
  fontSize: 14,
  fontWeight: 600,
  lineHeight: "14px",
  fontFamily: dashboardFontFamily,
  letterSpacing: 0,
  boxShadow: "none",
  "&:hover": { borderColor: "#c8d2e0", bgcolor: "#fff" },
  "& .MuiButton-endIcon": {
    ml: 0,
    mr: 0,
    width: 10,
    height: 16,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
};
const detailButtonIconSx = { width: 10, height: 16, fontSize: 16, color: "#000000" };

export default MonitoringDashboardCustomizedPage;
